/**
 * Сохранение версии черновика — `POST /api/v1/drafts/:id/versions` — и словарь его отказов.
 *
 * Файл появился, когда у маршрута стал второй вызывающий. Раньше эти четыре вещи жили в
 * `editor/SourceEditorPanel.tsx`; полоса визуала бьёт в ТОТ ЖЕ маршрут («Удалить картинку»,
 * «Загрузить свою») и получает те же 409. Две копии словаря отказов разошлись бы первыми:
 * у редактора `stale_base_version` уже разобран как ПОДТВЕРЖДЕНИЕ, а не ошибка, и полоса,
 * написавшая про то же самое «не удалось сохранить», сделала бы один и тот же отказ двумя
 * разными событиями на одном экране.
 *
 * Ключ `media` различает ТРИ случая наличием, а не значением (`listeners/ui.ts:636`):
 * ключа нет — картинка наследуется, `null` — снимается, `{media_id}` — прикрепляется.
 * Поэтому в аргументах он `undefined | null | {media_id}`, а не `string | null`.
 */

import type { MessageEntity } from '@smm/contracts';
import { ApiRequestError, post } from '../api/client.ts';

/** Единственное, что вызывающим нужно от ответа: появилась ли новая версия. */
export interface SaveResponse {
  readonly version_no: number;
}

export interface SaveVersionArgs {
  readonly draftId: string;
  readonly text: string;
  readonly entities: readonly MessageEntity[];
  /** Отсутствует — наследовать картинку; `null` — снять; объект — прикрепить. */
  readonly media?: { readonly media_id: string } | null;
  /** Версия, ИЗ КОТОРОЙ сделана правка (K-008). */
  readonly baseVersionNo: number;
}

/**
 * `link_preview_options` НЕ отправляется никогда: отсутствие ключа означает «карточка
 * следует за текстом», и бэкенд ставит её по новому медиа сам (`domain/human-edit.ts` §6).
 * Прислать ключ значило бы заморозить карточку от прошлой версии — в том числе на посте,
 * который картинку только что получил и права на карточку лишился.
 */
export function saveVersion(a: SaveVersionArgs): Promise<SaveResponse> {
  return post<SaveResponse>(`/api/v1/drafts/${a.draftId}/versions`, {
    text: a.text,
    entities: a.entities,
    base_version_no: a.baseVersionNo,
    ...(a.media === undefined ? {} : { media: a.media }),
  });
}

export function detailIssues(details: unknown): string[] {
  if (typeof details !== 'object' || details === null) return [];
  const raw = (details as Record<string, unknown>)['issues'];
  if (!Array.isArray(raw)) return [];
  const list: readonly unknown[] = raw;
  const out: string[] = [];
  for (const item of list) {
    if (typeof item !== 'object' || item === null) continue;
    const message = (item as Record<string, unknown>)['message'];
    const code = (item as Record<string, unknown>)['code'];
    if (typeof message !== 'string') continue;
    out.push(typeof code === 'string' ? `${message} (${code})` : message);
  }
  return out;
}

/**
 * Отказ «правка сделана из НЕ текущей версии» (K-008). Отличается от прочих 409 деталью
 * `reason`: пока человек решал, кто-то сохранил свою версию, и класть правку поверх неё
 * значило бы выбросить чужой текст без следа.
 */
export function isStaleBaseVersion(e: unknown): boolean {
  if (!(e instanceof ApiRequestError) || e.code !== 'state_conflict') return false;
  const d = e.details;
  if (typeof d !== 'object' || d === null) return false;
  return (d as Record<string, unknown>)['reason'] === 'stale_base_version';
}

/**
 * Коды отказа — словами. Молчаливое «не удалось сохранить» здесь недопустимо: человек обязан
 * понимать, почему правка не прошла, потому что три из четырёх причин он может устранить сам.
 */
export function saveErrorText(e: unknown): string {
  if (!(e instanceof ApiRequestError)) return e instanceof Error ? e.message : String(e);
  switch (e.code) {
    case 'state_conflict':
    case 'version_locked':
      return `${e.message}. На это содержание уже выпущен ключ публикации, поэтому текст заморожен. Снимите версию со слота — правка станет доступна снова.`;
    case 'not_found':
      return 'Черновика или его версий больше нет — экран открыт по устаревшей ссылке. Обновите список.';
    case 'invalid_for_telegram':
    case 'unprocessable_entity': {
      const list = detailIssues(e.details);
      return list.length === 0
        ? `Канонизация отвергла содержимое: ${e.message}.`
        : `Канонизация отвергла содержимое: ${list.join('; ')}.`;
    }
    default:
      return `${e.message} (${e.code}).`;
  }
}
